import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, LineChart, Sparkles, Briefcase, Building2, Tag, GraduationCap, Flag, Printer } from "lucide-react";
import { LOGO_URL, BRAND } from "../lib/brand";
import { useAuth } from "../contexts/AuthContext";
import { PortalNavButton } from "../components/PortalNavButton";

const CALENDLY_URL = "https://calendly.com/ramzybaroody";

export const CATALOG = [
  {
    code: "EXCEL-101",
    icon: LineChart,
    title: "Excel Fundamentals",
    prerequisite: null,
    overview:
      "A ground-up course for anyone who needs to be genuinely competent in Excel — no prior experience assumed. Designed to get a beginner to \u201cconfidently useful in a day-to-day office job\u201d level.",
    topics: [
      "Interface & navigation",
      "Cell referencing (relative vs. absolute)",
      "Core formulas: SUM, AVERAGE, COUNTIF, IF, nested IF",
      "VLOOKUP / XLOOKUP",
      "Data & conditional formatting",
      "Sorting & filtering",
      "Data cleaning: duplicates, text-to-columns, blanks & errors",
      "Intro to PivotTables & PivotCharts",
      "Basic chart creation for reporting",
    ],
    capstone:
      "Build a simple budget or tracking workbook from a raw data set.",
  },
  {
    code: "EXCEL-201",
    icon: Sparkles,
    title: "Advanced Excel with AI",
    prerequisite: "Excel Fundamentals or equivalent working knowledge",
    overview:
      "For students who already know core Excel and want to work at a professional/analyst level, with AI as a force multiplier rather than a crutch. Learn to use tools like Copilot in Excel and ChatGPT to write and debug complex formulas, generate PivotTable and dashboard layouts, auto-document workbooks, and build repeatable prompts for recurring reporting tasks.",
    topics: [
      "Advanced formulas: INDEX/MATCH, XLOOKUP with multiple criteria",
      "Array formulas, LAMBDA & named functions",
      "Power Query for cleaning & merging messy data sources",
      "Power Pivot & the data model for relational reports",
      "Dynamic arrays: FILTER, SORT, UNIQUE",
      "Dashboard design principles",
      "AI-assisted formula writing & debugging",
      "AI-generated layouts, documentation & reporting prompts",
    ],
    capstone:
      "Build an automated, AI-assisted reporting dashboard from a raw dataset.",
  },
  {
    code: "PM-301",
    icon: Briefcase,
    title: "Project Management with AI Integration",
    prerequisite: null,
    overview:
      "Practical project management — scoping, timelines, budgets, stakeholder and risk management, and the core differences between Waterfall and Agile/Scrum — with AI layered in as a working tool at every phase.",
    topics: [
      "Scoping, timelines & budgets",
      "Stakeholder & risk management",
      "Waterfall vs. Agile/Scrum",
      "Real PM artifacts: charters, RACI matrices, Gantt-style timelines, risk registers",
      "AI-drafted project charters & status reports",
      "AI meeting-note summaries into action items",
      "AI-flagged schedule & budget risks",
      "AI-assisted resource forecasting",
    ],
    capstone:
      "Manage a simulated project end-to-end using AI tools at each phase — initiation, planning, execution, monitoring, close-out.",
  },
  {
    code: "SYS-401",
    icon: Building2,
    title: "Business Systems & Processes (Industry-Specific)",
    prerequisite: null,
    overview:
      "Learn to design and build practical operational systems — trackers, dashboards, workflows — tailored to your own workplace, using Excel and AI tools together. Built around real industry case studies, with additional industry modules added as the course library grows.",
    topics: [
      "Insurance office: claims-status tracker & policy renewal/lapse dashboard",
      "Insurance office: agent commission calculator & complaint/escalation log",
      "Automated status flags & AI-generated summary reports",
      "Medical office: patient scheduling & no-show tracking",
      "Medical office: insurance claim & billing status trackers",
      "Supply/inventory reorder systems",
      "AI-assisted (HIPAA-conscious) summarization of intake notes",
    ],
    capstone:
      "Build one full system — tracker + dashboard + at least one AI-automated step — modeled on your actual job.",
  },
];

function CatalogNav({ isAuthed }) {
  return (
    <nav data-testid="catalog-nav" className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#0b1d3a]/85 border-b border-[#d4af37]/20">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between gap-6">
        <Link to="/" className="flex items-center gap-3" data-testid="catalog-logo-link">
          <img src={LOGO_URL} alt="CFCS" className="h-9 w-auto rounded-sm" />
        </Link>
        <div className="hidden md:flex items-center gap-7 text-sm text-[#fff8e1]/75">
          <Link to="/" className="hover:text-[#d4af37] transition-colors" data-testid="catalog-nav-home">Home</Link>
          <Link to="/catalog" className="text-[#d4af37] font-semibold" data-testid="catalog-nav-catalog">Course Catalog</Link>
          <Link to="/about" className="hover:text-[#d4af37] transition-colors" data-testid="catalog-nav-about">About</Link>
        </div>
        <div className="flex items-center gap-3">
          {isAuthed ? (
            <Link to="/dashboard" data-testid="catalog-nav-dashboard" className="text-sm text-[#d4af37] hover:underline">Open dashboard →</Link>
          ) : (
            <PortalNavButton testId="catalog-nav-signin" />
          )}
          <a href={CALENDLY_URL} target="_blank" rel="noreferrer" data-testid="catalog-nav-cta" className="hidden sm:inline-flex items-center gap-1.5 bg-[#d4af37] text-[#0b1d3a] px-4 py-2 rounded-md text-sm font-semibold hover:bg-[#e2bd44] transition-colors">
            Book walkthrough <ArrowRight className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>
    </nav>
  );
}

function PricingPill() {
  return (
    <span data-testid="pricing-pill" className="inline-flex items-center gap-1.5 rounded-full border border-[#d4af37] bg-[#d4af37]/10 px-3 py-1 text-xs font-semibold tracking-wide text-[#d4af37] whitespace-nowrap">
      <Tag className="w-3 h-3" /> Contact for pricing
    </span>
  );
}

function CatalogCourse({ course }) {
  return (
    <article data-testid={`catalog-course-${course.code}`} className="rounded-2xl overflow-hidden border-2 border-[#d4af37]/30 bg-[#11264c] hover:border-[#d4af37]/60 transition-colors">
      {/* Header */}
      <div className="bg-[#fff8e1] text-[#0b1d3a] px-6 md:px-8 py-6 border-b-2 border-[#d4af37]">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-4 min-w-0">
            <course.icon className="w-8 h-8 text-[#0b1d3a] shrink-0 mt-1" strokeWidth={1.5} />
            <div>
              <p className="text-xs font-bold tracking-[0.18em] uppercase text-[#7d6b32]">{course.code} · One-time enrollment fee</p>
              <h3 className="font-display text-2xl md:text-3xl font-bold mt-1 leading-tight">{course.title}</h3>
            </div>
          </div>
          <PricingPill />
        </div>
        {course.prerequisite && (
          <p data-testid={`prereq-${course.code}`} className="mt-4 inline-flex items-center gap-2 rounded-md bg-[#0b1d3a]/5 border border-[#0b1d3a]/15 px-3 py-1.5 text-xs font-semibold text-[#0b1d3a]/80">
            <GraduationCap className="w-3.5 h-3.5 text-[#7d6b32]" /> Prerequisite: {course.prerequisite}
          </p>
        )}
      </div>

      {/* Body */}
      <div className="p-6 md:p-8 space-y-6">
        <p className="text-[#fff8e1]/80 leading-relaxed">{course.overview}</p>

        <div>
          <p className="text-xs tracking-[0.2em] uppercase text-[#d4af37] font-semibold mb-3">What you'll learn</p>
          <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
            {course.topics.map((t) => (
              <li key={t} className="flex items-start gap-2.5 text-sm text-[#fff8e1]/75 leading-relaxed">
                <span className="mt-[7px] w-1.5 h-1.5 rounded-full bg-[#d4af37] shrink-0" />
                {t}
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-xl border border-[#d4af37]/30 bg-[#0b1d3a] p-5 flex items-start gap-4">
          <Flag className="w-5 h-5 text-[#d4af37] shrink-0 mt-0.5" strokeWidth={1.5} />
          <div>
            <p className="text-xs tracking-[0.2em] uppercase text-[#d4af37] font-semibold mb-1">Capstone</p>
            <p className="text-sm text-[#fff8e1]/80 leading-relaxed">{course.capstone}</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 pt-1">
          <Link to="/#contact" data-testid={`catalog-enroll-${course.code}`} className="inline-flex items-center gap-2 bg-[#d4af37] text-[#0b1d3a] px-5 py-2.5 rounded-md text-sm font-semibold hover:bg-[#e2bd44] transition-colors">
            Request enrollment <ArrowRight className="w-3.5 h-3.5" />
          </Link>
          <a href={CALENDLY_URL} target="_blank" rel="noreferrer" data-testid={`catalog-book-${course.code}`} className="inline-flex items-center gap-2 border-2 border-[#d4af37]/50 text-[#fff8e1] px-5 py-2.5 rounded-md text-sm font-semibold hover:border-[#d4af37] hover:bg-[#d4af37]/5 transition-colors">
            Book a walkthrough
          </a>
          <Link to={`/flyer/${course.code}`} data-testid={`catalog-flyer-${course.code}`} className="inline-flex items-center gap-2 border-2 border-[#d4af37]/50 text-[#fff8e1] px-5 py-2.5 rounded-md text-sm font-semibold hover:border-[#d4af37] hover:bg-[#d4af37]/5 transition-colors">
            <Printer className="w-3.5 h-3.5" /> Printable flyer
          </Link>
        </div>
      </div>
    </article>
  );
}

export default function Catalog() {
  const { user } = useAuth();

  return (
    <div data-testid="catalog-page" className="min-h-screen bg-[#0b1d3a] text-[#fff8e1]">
      <CatalogNav isAuthed={!!user} />

      {/* Hero */}
      <section className="pt-32 pb-14 px-6 lg:px-10">
        <div className="max-w-5xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#fff8e1]/60 hover:text-[#d4af37] transition-colors mb-8" data-testid="catalog-back-link">
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
          <p className="text-xs tracking-[0.25em] uppercase text-[#d4af37] mb-4 font-semibold">Course Catalog</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#fff8e1] leading-[1.08]">
            Available courses,<br /><span className="text-[#d4af37]">built for real work.</span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-[#fff8e1]/70 max-w-2xl leading-relaxed">
            Every course is hands-on, capstone-driven, and taught by a Ph.D. College Professor. Enrollment is a one-time fee — contact us for pricing tailored to your team size.
          </p>
        </div>
      </section>

      {/* Courses */}
      <section className="pb-24 px-6 lg:px-10">
        <div className="max-w-5xl mx-auto space-y-10">
          {CATALOG.map((c) => <CatalogCourse key={c.code} course={c} />)}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 lg:px-10 bg-[#0a1830] border-t border-[#d4af37]/15">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-[#fff8e1] mb-5">Not sure which course fits your team?</h2>
          <p className="text-[#fff8e1]/70 text-lg mb-8">Book a 15-minute walkthrough — we&apos;ll recommend the right starting point and come back with tailored pricing within one business day.</p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <a href={CALENDLY_URL} target="_blank" rel="noreferrer" data-testid="catalog-cta-calendly" className="inline-flex items-center gap-2 bg-[#d4af37] text-[#0b1d3a] px-7 py-3.5 rounded-md font-semibold hover:bg-[#e2bd44] transition-all shadow-[0_8px_30px_rgba(212,175,55,0.25)]">
              Book a walkthrough <ArrowRight className="w-4 h-4" />
            </a>
            <Link to="/#contact" data-testid="catalog-cta-contact" className="inline-flex items-center gap-2 border-2 border-[#d4af37]/50 text-[#fff8e1] px-7 py-3.5 rounded-md font-semibold hover:border-[#d4af37] hover:bg-[#d4af37]/5 transition-colors">
              Send a message
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#d4af37]/20 py-10 px-6 lg:px-10 bg-[#0a1830]">
        <div className="max-w-6xl mx-auto text-center">
          <img src={LOGO_URL} alt="CFCS" className="h-9 w-auto rounded-sm mx-auto mb-3" />
          <p className="text-xs text-[#fff8e1]/40">© {new Date().getFullYear()} {BRAND.name}. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
